import type { i18n as I18nInstance, ThirdPartyModule } from "i18next";

// The state i18next's backend connector keeps for a "language|namespace" file that is in flight.
const LOADING = 1;

const toArray = (value: string | readonly string[]): readonly string[] =>
  typeof value === "string" ? [value] : value;

/**
 * True while a file of the namespace is still being fetched for any language `t` can fall back
 * through (e.g. en-AU, then en).
 */
const isNamespaceLoading = (
  instance: I18nInstance,
  ns: string | readonly string[],
  lng?: string | readonly string[]
): boolean => {
  const { backendConnector, languageUtils } = instance.services;
  if (!backendConnector?.backend) return false;

  const languages: readonly string[] = lng
    ? toArray(lng).flatMap((language) => languageUtils.toResolveHierarchy(language))
    : instance.languages;

  return toArray(ns).some((namespace) =>
    languages.some((language) => backendConnector.state[`${language}|${namespace}`] === LOADING)
  );
};

/**
 * Keeps a namespace "not loaded" until every language in its fallback chain has finished loading.
 *
 * i18next reports a namespace as loaded as soon as the active language's file is stored. Our
 * regional files (en-AU, en-GB) only hold overrides, so they usually land before the English file
 * that holds most of the keys. react-i18next then stops suspending, and a page rendered in that gap
 * gets raw keys back from `t` - or a string where it expects an array, which crashes the page on a
 * first visit. A fetch that failed counts as finished, so a missing file can never suspend forever.
 */
export const fallbackAwareReadiness: ThirdPartyModule = {
  type: "3rdParty",
  init(instance) {
    const hasLoadedNamespace = instance.hasLoadedNamespace.bind(instance);

    // react-i18next decides whether to suspend through this method alone.
    // eslint-disable-next-line no-param-reassign
    instance.hasLoadedNamespace = (ns, options) =>
      hasLoadedNamespace(ns, options) && !isNamespaceLoading(instance, ns, options?.lng);
  },
};
